import React, { useEffect, useState } from 'react';
import { ScrollView, View, TouchableOpacity, Image, ActivityIndicator } from 'react-native';
import { translate } from '../../../services/translate';
import MyAppText from '../../MyAppText';
import { faLocationDot, faPlay, faSearchPlus, faUserFriends, faChevronUp, faChevronDown, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { faCalendarCheck } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';

import personDefaultPicture from '../../../../assets/person-default-picture.png';
import { TextMask } from 'react-native-masked-text';
import OccurrenceVideoModal from './OccurrenceVideoModal';
import { dispatchService } from '../../../services/central-api/dispatch';
import { Hoverable } from 'react-native-web-hover';
import OccurrenceCaptureHistoryModal from './OccurrenceCaptureHistoryModal';
import getBestContrastColor from '../../../services/best-contrast-color';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { faCameraCctv } from '@fortawesome/pro-solid-svg-icons';
import { MyImage } from '../../MyImage';
import { RootNavigation } from '../../../Routes';
import BasicModal from '../../BasicModal';

export default function OccurrenceFormFaceDetected({
    event, navigation, occurrenceId, occurrenceImage, triggerType
}: {
    event: FacialDetection;
    navigation: RootNavigation;
    occurrenceId: number;
    occurrenceImage: string;
    triggerType: 'face_detected' | 'missing_face_detected';
}) {
    const { styles, theme } = useStyles(stylesheet);
    const [isVideoModalVisible, setVideoModalVisible] = useState(false);
    const [isCaptureHistoryModalVisible, setCaptureHistoryModalVisible] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [showDetails, setShowDetails] = useState<boolean>(false);
    const [eventImageUrl, setEventImageUrl] = useState<string>('');
    const [refImageUrl, setRefImageUrl] = useState<string>();
    const [eventImageLoading, setEventImageLoading] = useState(false);
    const [refImageLoading, setRefImageLoading] = useState(false);

    async function fetchImageAsBlob(url: string) {
        const res = await fetch(url);
        const blob = await res.blob();
        return URL.createObjectURL(blob);
    }

    useEffect(() => {
        fetchImageAsBlob(occurrenceImage).then(imageUrl => {
            setEventImageUrl(imageUrl);
        });
    }, [occurrenceImage]);

    useEffect(() => {

        if (event.person) {
            event.person.facePictures = event.person.facePictures.filter(fp => {
                const eventCreatedAt = moment(event.createdAt);
                return moment(fp.createdAt).isBefore(eventCreatedAt) &&
                    (fp.deletedAt === null || moment(fp.deletedAt).isAfter(eventCreatedAt));
            });

            if (event.person.facePictures.length) {
                fetchImageAsBlob(event.person.facePictures[0].imageUrl).then(imageUrl => {
                    setRefImageUrl(imageUrl);
                });
            }
        }

    }, [event]);

    function PersonInfoInRow({ title, desc }: { title: string; desc: string | null; }) {

        return <View style={{ flexDirection: 'row' }}>
            <MyAppText style={styles.personInfoTitleText}>
                {title}:
            </MyAppText>
            <MyAppText style={styles.personInfoText}>
                {desc || ''}
            </MyAppText>
        </View>;
    }

    return (
        <ScrollView contentContainerStyle={{ rowGap: 10 }}>
            <View style={{ flexDirection: 'row' }}>
                <MyAppText style={styles.nameText}>
                    {event.personDocument ? `${translate('wanted')} ${event.personDocument}` : event.person?.name}
                </MyAppText>
                {event.person?.nickname ?
                    <View style={{ flexDirection: 'row', paddingTop: 5 }}>
                        <MyAppText style={styles.personInfoTitleText}>
                            {translate('nickname')}:
                        </MyAppText>
                        <MyAppText style={styles.personInfoText}>
                            {event.person.nickname}
                        </MyAppText>
                    </View>
                    : null
                }
            </View>
            <View style={{ flexDirection: 'row', gap: 8 }}>
                {event.person?.markers?.map((marker) =>
                    <View key={marker.id} style={[styles.marker, { backgroundColor: marker.color }]}>
                        <MyAppText style={styles.markerText(marker.color)}>
                            {marker.name}
                        </MyAppText>
                    </View>
                )}
            </View>
            <Hoverable>
                {({ hovered }) => (<>
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 }} >
                        <View>
                            <MyAppText style={styles.imageText}>
                                {translate('detection')}
                            </MyAppText>
                            <Image
                                style={styles.imageBox}
                                source={{ uri: eventImageUrl }}
                            />
                        </View>
                        <View>
                            <MyAppText style={styles.imageText}>
                                {translate('reference')}
                            </MyAppText>
                            <Image
                                style={styles.imageBox}
                                source={refImageUrl ?? personDefaultPicture}
                            />
                        </View>
                    </View>

                    {hovered &&
                        <View style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#00000080',
                        }}>
                            <TouchableOpacity
                                style={{
                                    flex: 1,
                                    justifyContent: 'center'
                                }}
                                onPress={async () => {
                                    setIsModalVisible(true);
                                }}
                            >
                                <FontAwesomeIcon icon={faSearchPlus} fontSize={28} color={'#FFFFFF'} />
                            </TouchableOpacity>
                        </View>
                    }
                </>)}
            </Hoverable>
            <MyAppText style={styles.confidenceText}>
                {event.confidence ?
                    translate('confidence') + ': ' + (Math.floor(Number(event.confidence) * 100)).toFixed(0) + '%'
                    : null
                }
            </MyAppText>
            <View style={styles.generalInformation}>
                <TouchableOpacity
                    style={styles.generalInformationDropdown}
                    onPress={() => setShowDetails(!showDetails)}>
                    <MyAppText>{translate('general_info')}</MyAppText>
                    <FontAwesomeIcon icon={showDetails ? faChevronUp : faChevronDown} color={theme.colors.iconColor} />
                </TouchableOpacity>
                <View style={styles.tableLine}></View>
            </View>
            {showDetails ? <>
                <View style={{ rowGap: 5 }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }} >
                            <FontAwesomeIcon icon={faPlay} style={{ width: 22 }} color={theme.colors.iconColor} />
                            <TouchableOpacity onPress={() => setVideoModalVisible(true)}>
                                <MyAppText style={styles.watchVideoText}>
                                    {translate('watchVideo')}
                                </MyAppText>
                            </TouchableOpacity>
                        </View>
                        <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }} >
                            <FontAwesomeIcon icon={faUserFriends} style={{ width: 22 }} color={theme.colors.iconColor} />
                            <TouchableOpacity onPress={() => setCaptureHistoryModalVisible(true)}>
                                <MyAppText style={styles.watchVideoText}>
                                    {translate('captureHistory')}
                                </MyAppText>
                            </TouchableOpacity>
                        </View>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }} >
                        <FontAwesomeIcon icon={faCameraCctv as IconDefinition} style={{ width: 22 }} color={theme.colors.iconColor} />
                        <TouchableOpacity onPress={() => {
                            navigation.navigate('Cameras', { screen: 'CameraPlayer', params: { id: event.camera.id } });
                        }}>
                            <MyAppText style={[styles.detectionInfoText, { textDecorationLine: 'underline' }]}>
                                {event.camera.title}
                            </MyAppText>
                        </TouchableOpacity>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }}>
                        <FontAwesomeIcon icon={faCalendarCheck} style={{ width: 22 }} color={theme.colors.iconColor} />
                        <MyAppText style={styles.detectionInfoText}>
                            {moment(new Date(event.time)).format('DD/MM/YYYY HH:mm:ss')}
                        </MyAppText>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }}>
                        <FontAwesomeIcon icon={faLocationDot} style={{ width: 22 }} color={theme.colors.iconColor} />
                        <MyAppText style={styles.detectionInfoText}>
                            {event.camera.address}
                        </MyAppText>
                    </View>
                </View>

                <View style={{ flexDirection: 'row' }}>
                    <View style={styles.documentContainer}>
                        <MyAppText style={styles.personDocumentInfoText}>CPF:</MyAppText>
                        <TextMask
                            style={[styles.personDocumentInfoText, { fontFamily: 'Open Sans' }]}
                            value={event.personDocument || event.person?.document || ''}
                            type={'cpf'}
                        />
                    </View>
                    <View style={styles.documentContainer}>
                        <MyAppText style={styles.personDocumentInfoText}>
                            {translate('personRegister')}:
                        </MyAppText>
                        <MyAppText style={styles.personDocumentInfoText}>
                            {event.person?.register}
                        </MyAppText>
                    </View>
                    <View style={styles.documentContainer}>
                        <MyAppText style={styles.personDocumentInfoText}>
                            {translate('registeredOn')}:
                        </MyAppText>
                        <MyAppText style={styles.personDocumentInfoText}>
                            {moment(new Date(event.person?.createdAt || '')).format('DD/MM/YYYY HH:mm:ss')}
                        </MyAppText>
                    </View>
                </View>
                {
                    event.person?.comment ?
                        <View style={{ paddingVertical: 20 }}>
                            <MyAppText style={styles.personInfoTitleText}>
                                {translate('additionalComments')}:
                            </MyAppText>
                            <MyAppText style={styles.personInfoText}>
                                {event.person.comment}
                            </MyAppText>
                        </View>
                        : null
                }
                <View style={{ paddingVertical: 20 }}>
                    {event.person?.city ?
                        <View style={{ flexDirection: 'row' }}>
                            <MyAppText style={styles.personInfoTitleText}>
                                {translate('city')}:
                            </MyAppText>
                            <MyAppText style={styles.personInfoText}>
                                {event.person.city}/{event.person.state}
                            </MyAppText>
                        </View>
                        : null
                    }
                </View>

                {
                    triggerType == 'face_detected' ?
                        <>
                            <MyAppText style={[styles.nameText, { paddingBottom: 5 }]}>
                                {translate('arrestWarrants')}:
                            </MyAppText>
                            {
                                event.person?.lawsuits?.map((lawsuit) => (
                                    <View key={lawsuit.id} style={{ paddingBottom: 20 }}>
                                        <PersonInfoInRow
                                            title={translate('situation')}
                                            desc={lawsuit.situation}
                                        />
                                        <PersonInfoInRow
                                            title={translate('arrestWarrantNumber')}
                                            desc={lawsuit.arrestWarrantNumber}
                                        />
                                        <PersonInfoInRow
                                            title={translate('shippingDate')}
                                            desc={lawsuit.shippingDate}
                                        />
                                        <PersonInfoInRow
                                            title={translate('expirationDate')}
                                            desc={lawsuit.expirationDate}
                                        />
                                        <PersonInfoInRow
                                            title={translate('lawsuitNumber')}
                                            desc={lawsuit.lawsuitNumber}
                                        />
                                        <PersonInfoInRow
                                            title={translate('prisonKind')}
                                            desc={lawsuit.prisonKind}
                                        />
                                        <PersonInfoInRow
                                            title={translate('magistrate')}
                                            desc={lawsuit.magistrate}
                                        />
                                        <PersonInfoInRow
                                            title={translate('issuingBody')}
                                            desc={lawsuit.issuingBody}
                                        />
                                        <PersonInfoInRow
                                            title={translate('county')}
                                            desc={lawsuit.lawsuitCity}
                                        />
                                    </View>
                                ))
                            }
                        </>
                        : null}</>
                : <></>}

            <OccurrenceVideoModal
                occurrenceId={occurrenceId || 0}
                occurrenceTime={new Date(event.time).getTime()}
                isModalVisible={isVideoModalVisible}
                setModalVisible={setVideoModalVisible}
                apiService={dispatchService}
            />
            <OccurrenceCaptureHistoryModal
                occurrenceId={occurrenceId || 0}
                isModalVisible={isCaptureHistoryModalVisible}
                setModalVisible={setCaptureHistoryModalVisible}
            />

            {/* Zoom image modal */}
            <BasicModal
                isModalVisible={isModalVisible}
                setModalVisible={setIsModalVisible}
                width='extra-large'
                cancelButton={{
                    customText: translate('close'),
                }}
                title={translate(triggerType)}
            >
                {(eventImageLoading || refImageLoading) && <ActivityIndicator style={{ height: '90%' }} size='large' color={theme.colors.loadingColor} />}
                <View style={[{ flexDirection: 'row', gap: 15 }, (eventImageLoading || refImageLoading) && { opacity: 0 }]} >
                    <View style={{ flex: 1 }}>
                        <MyAppText style={styles.imageText}>
                            {translate('detection')}
                        </MyAppText>
                        <MyImage
                            imageUrl={eventImageUrl}
                            onLoadStart={() => setEventImageLoading(true)}
                            onLoadEnd={() => setEventImageLoading(false)}
                            onLoad={() => setEventImageLoading(false)}
                        />
                    </View>
                    <View style={{ flex: 1 }}>
                        <MyAppText style={styles.imageText}>
                            {translate('reference')}
                        </MyAppText>
                        <MyImage
                            imageUrl={refImageUrl ?? personDefaultPicture}
                            onLoadStart={() => setRefImageLoading(true)}
                            onLoadEnd={() => setRefImageLoading(false)}
                            onLoad={() => setRefImageLoading(false)}
                        />
                    </View>
                </View>
            </BasicModal>
        </ScrollView >
    );
}

const stylesheet = createStyleSheet(theme => ({
    container: {
        padding: 16,
        backgroundColor: theme.colors.container.background,
        borderRadius: 8,
        borderColor: theme.colors.container.border,
        borderWidth: 1,
    },
    imageText: {
        color: theme.colors.card.detailText,
        paddingBottom: 5,
        fontSize: 11,
    },
    detectionInfoText: {
        color: theme.colors.labelColor,
        fontSize: 12
    },
    confidenceText: {
        color: theme.colors.card.detailText,
        fontSize: 14,
        paddingVertical: 10
    },
    nameText: {
        color: theme.colors.textColor,
        fontWeight: 'bold',
        fontSize: 20
    },
    personInfoTitleText: {
        color: theme.colors.textColor,
        fontWeight: 'bold',
        fontSize: 16
    },
    personInfoText: {
        color: theme.colors.textColor,
        fontSize: 16
    },
    documentContainer: {
        paddingRight: 35
    },
    personDocumentInfoText: {
        color: theme.colors.textColor,
        fontSize: 14
    },
    marker: {
        borderRadius: 8,
        height: 16,
        paddingLeft: 10,
        paddingRight: 10,
    },
    markerText: (tagColor: string) => ({
        fontWeight: '500',
        color: getBestContrastColor(tagColor),
        fontSize: 11
    }),
    watchVideoText: {
        color: theme.colors.labelColor,
        fontSize: 16,
        textDecorationLine: 'underline'
    },
    tableLine: {
        borderBottomColor: theme.colors.card.border,
        borderBottomWidth: 1,
        flex: 1
    },
    generalInformation: {
        flexDirection: 'column',
        paddingHorizontal: 5,
        flex: 1,
        gap: 10
    },
    generalInformationDropdown: {
        flexDirection: 'row',
        gap: 10,
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: 1
    },
    imageBox: {
        width: 200,
        height: 200,
        resizeMode: 'cover',
        borderRadius: 5,
        overflow: 'hidden'
    }
}));
